import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowDownUp,
  ArrowUp,
  ArrowDown,
  Pencil,
  CheckIcon,
  X as XIcon,
} from 'lucide-react';
import { useTable, type SortDirection } from '.';

export type TableCell = {
  value: string | number | ReactNode;
  editValue?: string | number;
};

export type TableRow = {
  id: TableCell;
  [key: string]: TableCell;
};

export interface TableColumn {
  header: string;
  key: string;
  isSortable?: boolean;
  width?: number; // Column width is specified in rem units.;
  isEditable?: boolean;
  editableType?: 'text' | 'number';
  textShade?: 'light' | 'dark' | 'default';
}

export interface TableProps {
  rows: TableRow[];
  columns: TableColumn[];
  onSort?: (key: string, direction: SortDirection) => void;
  onEditSubmit?: (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => void;
  className?: string;
  emptyMessage?: string;
  tableControls?: ReturnType<typeof useTable>;
  totalRowsCount?: number;
  name?: string;
  minWidth?: number;
}

const PIXELS_PER_REM = 16;
const MIN_EDIT_COLUMN_WIDTH_REM = 10;

export function Table({
  rows,
  columns,
  onSort,
  className = '',
  emptyMessage = 'No data available',
  onEditSubmit,
  tableControls,
  totalRowsCount,
  name,
  minWidth,
}: TableProps) {
  const internalControls = useTable({ onSort, onEditSubmit });
  const controls = tableControls || internalControls;
  const thRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const {
    sortKey,
    sortDirection,
    editing,
    isSubmittingEdit,
    handleSort,
    handleEditContinue,
    handleEditClick,
    handleEditDone,
  } = controls;

  useEffect(() => {
    function updateColumnWidth() {
      const res: Record<string, number> = {};
      Object.keys(thRefs.current).forEach((key) => {
        const columnWidth =
          thRefs.current[key]?.getBoundingClientRect()?.width || 0;
        const widthInRem = columnWidth / PIXELS_PER_REM;
        res[key] = widthInRem;
      });
      setColumnWidths(res);
    }
    updateColumnWidth();
    window.addEventListener('resize', updateColumnWidth);
    return () => {
      window.removeEventListener('resize', updateColumnWidth);
    };
  }, [columns]);

  const getSortIcon = (columnKey: string) => {
    if (sortKey !== columnKey) {
      return <ArrowDownUp size={14} className="text-neut-400" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp size={14} className="ml-1 text-neut-600" />;
    } else if (sortDirection === 'desc') {
      return <ArrowDown size={14} className="ml-1 text-neut-600" />;
    }

    return null;
  };

  if (rows.length === 0) {
    return (
      <div className={`border border-gray-200 rounded-lg ${className}`}>
        <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
      </div>
    );
  }
  const columnShadeClass = {
    light: 'text-neut-500',
    dark: 'text-neut-900 font-medium',
    default: 'text-neut-700',
  };

  function getColumnWidthInRem(column: TableColumn): string | undefined {
    if (editing.columnKey !== column.key)
      return column.width ? column.width + 'rem' : undefined;
    const currentWidth = column.width || columnWidths[column.key];
    return currentWidth > MIN_EDIT_COLUMN_WIDTH_REM
      ? currentWidth + 'rem'
      : MIN_EDIT_COLUMN_WIDTH_REM + 'rem';
  }

  return (
    <table
      style={{ minWidth: (minWidth || 0) + 'rem' }}
      className={`w-full table-fixed ${className} overflow-x-auto`}
    >
      <thead className="bg-neut-50 border-b border-neut-200">
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              ref={(el) => {
                thRefs.current[column.key] = el;
              }}
              className={`px-4 py-3 text-left text-xs text-neut-600 capitalize font-medium  ${column.isSortable ? 'cursor-pointer hover:bg-neut-100' : ''}`}
              style={{ width: getColumnWidthInRem(column) }}
              onClick={() => column.isSortable && handleSort(column.key)}
            >
              <div className="flex items-center justify-start gap-1">
                {column.header}
                {column.isSortable && getSortIcon(column.key)}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {rows.map((row, rowIndex) => (
          <Fragment key={rowIndex}>
            <tr className="hover:bg-neut-50">
              {columns.map((column, idx) => (
                <td
                  key={column.key}
                  className={`px-4 py-3 text-sm whitespace-normal group ${columnShadeClass[column.textShade ?? 'default']}`}
                >
                  {editing.isEditing &&
                  editing.columnKey === column.key &&
                  editing.rowId === row.id.value ? (
                    <form
                      className="flex items-center gap-2"
                      onSubmit={handleEditContinue}
                    >
                      <input
                        data-testid={`input-${column.key}-${rowIndex}`}
                        name="editTableInput"
                        autoFocus
                        type={
                          column.editableType === 'number' ? 'number' : 'text'
                        }
                        step={
                          column.editableType === 'number' ? 'any' : undefined
                        }
                        defaultValue={
                          row[column.key].editValue ??
                          (row[column.key].value as string)
                        }
                        className="focus:ring-prim focus:ring-2 hover:ring-prim outline-1 outline-prim rounded-sm px-2 py-1 w-24 text-sm "
                      />

                      <button
                        className="text-success-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isSubmittingEdit}
                        type="submit"
                        data-testid={`check-button-${column.key}-${rowIndex}`}
                      >
                        <CheckIcon size={16} />
                      </button>
                      <button
                        className="text-error-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        type="button"
                        disabled={isSubmittingEdit}
                        onClick={handleEditDone}
                        data-testid={`cancel-button-${rowIndex}-${idx}`}
                      >
                        <XIcon size={16} />
                      </button>
                    </form>
                  ) : (
                    <div
                      className="flex gap-3 items-center break-words"
                      data-testid={`cell-${column.key}-${rowIndex}`}
                    >
                      {row[column.key].value}
                      {column.isEditable ? (
                        <button
                          data-testid={`edit-${column.key}-${rowIndex}`}
                          className="opacity-0 group-hover:opacity-100 hover:text-success-500 cursor-pointer"
                          onClick={() =>
                            handleEditClick(column.key, row.id.value as string)
                          }
                        >
                          <Pencil size={14} />
                        </button>
                      ) : null}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          </Fragment>
        ))}
      </tbody>
      <tfoot>
        {totalRowsCount !== undefined ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-6 py-3 border-t border-neut-border bg-neut-50"
            >
              <p className="text-xs text-neut-sec">
                Showing <span className="font-semibold">{rows.length}</span> of{' '}
                <span className="font-semibold">{totalRowsCount}</span> {name}
              </p>
            </td>
          </tr>
        ) : null}
      </tfoot>
    </table>
  );
}
